import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link as RouterLink } from "react-router-dom";
import { Box, Typography, Paper, Divider, Chip, Stack } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PaymentIcon from "@mui/icons-material/Payment";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { useGetInvoiceById } from "../../hooks/api/useInvoice";
import { useGetOrder } from "../../hooks/api/useSalesOrder";
import { useGetCustomerList } from "../../hooks/api/useCustomer";
import { usePrintOrderLineDisplayNames } from "../../hooks/usePrintOrderLineDisplayNames";
import {
  BRAND_COLOR,
  BRAND_HOVER,
  PORTAL_PREFIX,
  PAGE_PORTAL_ORDERS_SALES_DETAIL,
  PAGE_PORTAL_ORDERS_PAYMENT_DETAIL,
  KivuI18nContext,
  localizedPath,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import PrintButton from "../common/PrintButton/PrintButton";
import BrandButton from "../common/BrandButton/BrandButton";
import PrintableContent from "../common/PrintableContent/PrintableContent";
import NoInvoiceDetailFound from "./NoInvoiceDetailFound";
import CommonLoader from "../common/CommonLoader/CommonLoader";
import {
  formatDate,
  formatDateTime,
  getPaymentStatusColor
} from "../Orders/utils/orderUtils";
import { InvoiceStatus } from "./enums/invoice";
import { PaymentMethodLabels } from "../Payment/enums/paymentMethod";
import CreatePaymentModal from "../Payment/CreatePaymentModal";
import { useReportError } from "../../hooks/useReportError";
import { useFormatCurrency } from "../../hooks/useFormatCurrency";
import OpaqueIdOrText from "../common/OpaqueIdOrText";
import InvoicePrintLayout from "../PrintDocuments/InvoicePrintLayout";
import {
  buildInvoicePrintLayoutProps,
  resolvePrintStoreId
} from "../PrintDocuments/printDocumentMappers";
import type { Order } from "../Orders/types/salesOrder";

const InvoiceDetail: React.FC = () => {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const navigate = useNavigate();
  const { t } = useKivunovaTranslation();
  const formatCurrency = useFormatCurrency();
  const { language, defaultLang } = useContext(KivuI18nContext) || {};

  const { invoiceDetail } = useSelector((state: RootState) => state.invoices);
  const userDetails = useSelector((state: RootState) => state.user.userDetails);
  const companyName = useSelector(
    (state: RootState) => state.company.companyName
  );
  const storeList = useSelector((state: RootState) => state.store.storeList);
  const customerList = useSelector(
    (state: RootState) => state.customers.customerList
  );
  const getInvoiceById = useGetInvoiceById();
  const getOrder = useGetOrder();
  useGetCustomerList(userDetails?.companyId ?? undefined);
  const reportError = useReportError();
  const printContentRef = useRef<HTMLDivElement>(null);
  const [openCreatePayment, setOpenCreatePayment] = React.useState(false);
  const [orderForPrint, setOrderForPrint] = useState<Order | null>(null);
  const { displayNameByItemId } = usePrintOrderLineDisplayNames(orderForPrint);

  useEffect(() => {
    if (invoiceId) {
      (async () => {
        try {
          await getInvoiceById.mutateAsync({ invoiceId });
        } catch (err) {
          reportError("Failed to fetch invoice detail (UI)", err, {
            component: "InvoiceDetail",
            invoiceId: invoiceId ?? ""
          });
        }
      })();
    }
  }, [invoiceId, reportError]);

  useEffect(() => {
    if (
      !invoiceDetail ||
      invoiceDetail.invoiceId !== invoiceId ||
      !invoiceDetail.orderId
    ) {
      setOrderForPrint(null);
      return;
    }
    let cancelled = false;
    setOrderForPrint(null);
    (async () => {
      try {
        const order = await getOrder.mutateAsync({
          orderId: invoiceDetail.orderId
        });
        if (!cancelled) setOrderForPrint(order);
      } catch (err) {
        reportError("Failed to fetch order for invoice print", err, {
          component: "InvoiceDetail",
          orderId: invoiceDetail.orderId
        });
        if (!cancelled) setOrderForPrint(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [invoiceDetail, invoiceId, getOrder.mutateAsync, reportError]);

  const invoicePrintProps = useMemo(() => {
    if (!invoiceDetail || invoiceDetail.invoiceId !== invoiceId) return null;
    const customer = invoiceDetail.customerId
      ? customerList.find((c) => c.id === invoiceDetail.customerId)
      : undefined;
    const storeId = resolvePrintStoreId(orderForPrint, invoiceDetail);
    return buildInvoicePrintLayoutProps(
      invoiceDetail,
      orderForPrint,
      companyName,
      storeList.find((s) => s.id === storeId),
      customer,
      displayNameByItemId,
      t
    );
  }, [
    invoiceDetail,
    invoiceId,
    orderForPrint,
    companyName,
    storeList,
    customerList,
    displayNameByItemId,
    t
  ]);

  if (getInvoiceById.isPending) {
    return (
      <CommonLoader
        loadingMessage={t(
          "fetching-invoice-details",
          "Fetching invoice details..."
        )}
      />
    );
  }

  if (!invoiceDetail || invoiceDetail.invoiceId !== invoiceId) {
    return <NoInvoiceDetailFound />;
  }

  const canRecordPayment =
    invoiceDetail.status === InvoiceStatus.UNPAID ||
    invoiceDetail.status === InvoiceStatus.PARTIAL_PAID;

  const salesOrderDetailPath = localizedPath(
    `/${PORTAL_PREFIX}/${PAGE_PORTAL_ORDERS_SALES_DETAIL.replace(
      ":orderId",
      invoiceDetail.orderId
    )}`,
    language,
    defaultLang
  );

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box
        sx={{
          mb: 2.5,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: { xs: 1, sm: 1.25 }
        }}
      >
        <Box
          component="button"
          type="button"
          onClick={() => navigate(-1)}
          data-testid="invoice-detail-back"
          aria-label={t("back-to-invoices", "Back to Invoices")}
          sx={{
            display: { xs: "inline-flex", md: "none" },
            alignSelf: "flex-start",
            alignItems: "center",
            gap: 0.35,
            py: 0,
            mr: "auto",
            border: "none",
            background: "none",
            cursor: "pointer",
            font: "inherit",
            color: BRAND_COLOR,
            fontWeight: 500,
            fontSize: "0.8125rem",
            lineHeight: 1.2,
            textDecoration: "none",
            opacity: 0.92,
            transition: "opacity 0.15s ease, color 0.15s ease",
            "&:hover": {
              color: BRAND_HOVER,
              opacity: 1
            }
          }}
        >
          <ArrowBackIcon sx={{ fontSize: 16, flexShrink: 0 }} />
          {t("back", "Back")}
        </Box>

        <Box
          sx={{
            display: { xs: "grid", md: "flex" },
            gridTemplateColumns: "minmax(0, 1fr)",
            minWidth: 0,
            width: { xs: "100%", md: "100%" },
            maxWidth: {
              xs: "min(100%, 280px)",
              sm: "min(100%, 280px)",
              md: "none"
            },
            mx: { xs: "auto", md: 0 },
            gap: { xs: 1, sm: 1.25, md: 2 },
            justifyContent: { md: "flex-end" },
            alignItems: { md: "center" },
            flexWrap: { md: "wrap" },
            justifyItems: "stretch",
            "& > *": {
              width: { xs: "100%", md: "auto" },
              minHeight: { xs: 36, md: "auto" }
            }
          }}
        >
          {canRecordPayment && (
            <BrandButton
              size="small"
              startIcon={<PaymentIcon />}
              onClick={() => setOpenCreatePayment(true)}
            >
              {t("record-payment", "Record Payment")}
            </BrandButton>
          )}
          <PrintButton
            size="small"
            targetRef={printContentRef}
            title={`${t("invoice", "Invoice")}: ${invoiceDetail.invoiceNumber}`}
            label={t("print-invoice", "Print Invoice")}
          />
        </Box>
      </Box>

      {/* Printable Content */}
      <PrintableContent id="invoice-printable-content" ref={printContentRef}>
        <Box sx={{ display: "none", "@media print": { display: "block" } }}>
          {invoicePrintProps ? (
            <InvoicePrintLayout {...invoicePrintProps} />
          ) : null}
        </Box>

        <Box sx={{ "@media print": { display: "none !important" } }}>
          {/* Invoice Info */}
          <Paper className="invoice-content" sx={{ p: 3, mb: 3 }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography variant="h4" fontWeight="bold">
                {invoiceDetail.invoiceNumber}
              </Typography>
              <Chip
                label={invoiceDetail.status}
                color={getPaymentStatusColor(invoiceDetail.status)}
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            <Stack spacing={2}>
              {/* Order reference */}
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {t("order-number", "Order Number")}:
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {invoiceDetail.orderNumber}
                </Typography>
              </Box>

              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                gap={1}
              >
                <Typography variant="body2" color="text.secondary">
                  {t("order", "Order")}:
                </Typography>
                <Typography
                  component={RouterLink}
                  to={salesOrderDetailPath}
                  variant="body2"
                  fontWeight="medium"
                  sx={{
                    color: BRAND_COLOR,
                    textDecoration: "underline",
                    textUnderlineOffset: 2,
                    textAlign: "right",
                    "&:hover": { color: BRAND_HOVER }
                  }}
                >
                  {t("view-order", "View Order")}
                </Typography>
              </Box>

              {/* Customer */}
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {t("customer", "Customer")}:
                </Typography>
                <OpaqueIdOrText value={invoiceDetail.customerId} />
              </Box>

              {/* Dates */}
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {t("issue-date", "Issue Date")}:
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {formatDate(invoiceDetail.issueDate)}
                </Typography>
              </Box>

              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {t("created", "Created")}:
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {formatDateTime(invoiceDetail.createdAt)}
                </Typography>
              </Box>
            </Stack>
          </Paper>

          {/* Payments Section */}
          {invoiceDetail.payments.length > 0 && (
            <Paper className="invoice-content" sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" fontWeight="bold" mb={2}>
                {t("payments", "Payments")} ({invoiceDetail.payments.length})
              </Typography>
              <Stack spacing={1.5}>
                {invoiceDetail.payments.map((payment) => (
                  <Box
                    key={payment.paymentId}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{
                      py: 1.5,
                      px: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      "&:hover": { backgroundColor: "action.hover" }
                    }}
                  >
                    <Box display="flex" flexDirection="column" gap={0.5}>
                      <Typography
                        component={RouterLink}
                        to={localizedPath(
                          `/${PORTAL_PREFIX}/${PAGE_PORTAL_ORDERS_PAYMENT_DETAIL.replace(
                            ":paymentId",
                            payment.paymentId
                          )}`,
                          language,
                          defaultLang
                        )}
                        variant="body2"
                        fontWeight="medium"
                        sx={{
                          color: BRAND_COLOR,
                          textDecoration: "underline",
                          textUnderlineOffset: 2,
                          "&:hover": { color: BRAND_HOVER }
                        }}
                      >
                        {t("view-payment", "View Payment")}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(payment.paymentDate)} •{" "}
                        {PaymentMethodLabels[payment.paymentMethod] ||
                          payment.paymentMethod}
                        {payment.reference && ` • ${payment.reference}`}
                      </Typography>
                    </Box>
                    <Typography variant="body2" fontWeight="bold">
                      {formatCurrency(payment.amount)}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Paper>
          )}

          {/* Total Amount / Financial Summary */}
          <Paper className="invoice-content" sx={{ p: 3, mb: 3 }}>
            <Stack spacing={2}>
              <Divider />
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {t("subtotal", "Subtotal")}:
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {formatCurrency(invoiceDetail.subtotalAmount)}
                </Typography>
              </Box>

              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {t("discount", "Discount")}:
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight="medium"
                  color="error.main"
                >
                  {formatCurrency(invoiceDetail.discountAmount ?? 0)}
                </Typography>
              </Box>

              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {t("tax", "Tax")}:
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {formatCurrency(invoiceDetail.taxAmount)}
                </Typography>
              </Box>

              <Divider />

              <Box display="flex" justifyContent="space-between">
                <Typography variant="h6" fontWeight="bold">
                  {t("total-amount", "Total Amount")}:
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  {formatCurrency(invoiceDetail.totalAmount)}
                </Typography>
              </Box>

              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {t("amount-paid", "Amount Paid")}:
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight="medium"
                  color="success.main"
                >
                  {formatCurrency(invoiceDetail.paidAmount)}
                </Typography>
              </Box>

              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {t("remaining-amount", "Remaining Amount")}:
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  color={
                    invoiceDetail.remainingAmount > 0
                      ? "error.main"
                      : "success.main"
                  }
                >
                  {formatCurrency(invoiceDetail.remainingAmount)}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Box>
      </PrintableContent>

      {/* Create Payment Modal — outside PrintableContent to avoid print inclusion */}
      {openCreatePayment && (
        <CreatePaymentModal
          open={openCreatePayment}
          onClose={() => setOpenCreatePayment(false)}
          invoice={invoiceDetail}
          onPaymentSuccess={
            invoiceId
              ? () => getInvoiceById.mutateAsync({ invoiceId })
              : undefined
          }
        />
      )}
    </Box>
  );
};

export default InvoiceDetail;
